import React from 'react';
import { AlertTriangle, CheckCircle2, FileText, Trash2 } from 'lucide-react';
import { JournalImpactPreview as JournalImpactPreviewData } from '../../services/blog/blogDowngradeService';

interface JournalImpactPreviewProps {
  impact: JournalImpactPreviewData | null;
  loading?: boolean;
  /** Headline-Variante: passend zum Anwendungsfall (Downgrade vs. Remove vs. Cancel) */
  variant?: 'downgrade' | 'remove' | 'cancel';
}

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

/**
 * Wiederverwendbare Vorschau-Komponente: zeigt dem User vor jeder unwiderruflichen
 * Aktion (Downgrade-Bestätigung, Channel-Remove, Cancel/Expire) klar und mit
 * konkreten Beispielen, welche seiner Notizen betroffen sind.
 *
 * Wichtig: Notiz-Texte bleiben IMMER vollständig erhalten -- nur die Verlinkungen
 * werden zu toten Pillen ("Quelle nicht mehr verfügbar"). Das wird hier auch
 * eindeutig kommuniziert.
 */
const JournalImpactPreview: React.FC<JournalImpactPreviewProps> = ({ impact, loading = false, variant = 'remove' }) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700
                      bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-600 dark:text-gray-400">
        Vorschau wird geladen...
      </div>
    );
  }

  if (!impact) {
    return null;
  }

  const { affectedEntryCount, affectedReferenceCount, sampleEntries } = impact;

  // Fall 1: Keine Notizen betroffen -- Entwarnung anzeigen.
  if (affectedEntryCount === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800
                      bg-emerald-50 dark:bg-emerald-900/20 p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-emerald-800 dark:text-emerald-200">
            Keine deiner Notizen ist betroffen
          </p>
          <p className="text-emerald-700 dark:text-emerald-300 mt-0.5">
            Du hast keine Verlinkungen zu Videos der entfernten Kanäle in deinen Journal-Einträgen.
          </p>
        </div>
      </div>
    );
  }

  // Fall 2: Notizen betroffen -- Klartext-Folgen + Beispiele.
  const headline =
    variant === 'downgrade'
      ? 'Was passiert mit deinen Notizen nach dem Downgrade?'
      : variant === 'cancel'
      ? 'Was passiert mit deinen Notizen zum Periodenende?'
      : 'Was passiert mit deinen Notizen?';

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800
                    bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-900 dark:text-amber-100">{headline}</p>
        </div>
      </div>

      <ul className="text-sm text-amber-900 dark:text-amber-100 space-y-1 pl-8 list-disc">
        <li>
          <strong>Deine Notiz-Texte bleiben vollständig erhalten</strong> -- nichts wird gelöscht.
        </li>
        <li>
          Verlinkungen zu Videos der entfernten Kanäle werden in deinen Notizen durch ein graues
          Mülleimer-Symbol ersetzt (&bdquo;Quelle nicht mehr verfügbar&ldquo;).
        </li>
        <li>
          Bei Re-Abonnement musst du diese Verlinkungen neu setzen -- sie kehren nicht automatisch zurück.
        </li>
      </ul>

      <div className="text-sm text-amber-900 dark:text-amber-100 pl-8">
        <p className="font-medium">
          Konkret betroffen: {affectedEntryCount}{' '}
          {affectedEntryCount === 1 ? 'deiner Notizen' : 'deiner Notizen'},{' '}
          {affectedReferenceCount}{' '}
          {affectedReferenceCount === 1 ? 'Verlinkung' : 'Verlinkungen'} werden gekappt.
        </p>
      </div>

      {sampleEntries.length > 0 && (
        <div className="pl-8 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-800 dark:text-amber-200">
            Beispiele aus deinen Notizen
          </p>
          <ul className="space-y-2">
            {sampleEntries.map((entry) => (
              <li
                key={entry.entryId}
                className="rounded-md bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-700/60 p-3"
              >
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                      {entry.contentPreview}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatDate(entry.createdAt)}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" />
                        {entry.affectedMarkers.length}{' '}
                        {entry.affectedMarkers.length === 1
                          ? 'Verlinkung wird tot'
                          : 'Verlinkungen werden tot'}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {affectedEntryCount > sampleEntries.length && (
            <p className="text-xs text-amber-800 dark:text-amber-200 italic">
              ...und {affectedEntryCount - sampleEntries.length} weitere Notizen
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default JournalImpactPreview;
